import { ArrowRight } from 'lucide-react';

import type { IDecisionFrameworkConfig, IDecisionFrameworkRow } from '../_data/decision-framework';
import { cn } from '@/app/_utils/cn';

import FunnelCtaNavigation from './FunnelCtaNav';

interface IDecisionFrameworkItemProps {
  row: IDecisionFrameworkRow;
}

const DecisionFrameworkItem = ({ row }: IDecisionFrameworkItemProps) => {
  const emphasize = Boolean(row.emphasize);

  return (
    <li className='list-none'>
      <div
        tabIndex={-1}
        className={cn(
          'rounded-2xl bg-white',
          'transition-[box-shadow,border-color] duration-200 ease-out motion-reduce:transition-none',
          'cursor-default',
          emphasize
            ? 'decision-framework-card--featured'
            : 'shadow-sm hover:shadow-[0_12px_40px_-16px_rgba(0,0,0,0.1)] motion-reduce:hover:shadow-none',
        )}
      >
        <div
          className={cn(
            'flex flex-col gap-0 p-4 sm:p-5',
            'md:grid md:min-h-22 md:grid-cols-[minmax(0,6.5fr)_minmax(0,1fr)_minmax(0,2.85fr)] md:items-stretch md:gap-0 md:p-7',
          )}
        >
          <div className='min-w-0 border-b border-border/45 pb-4 md:border-b-0 md:pb-0 md:pr-5'>
            <span className='text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
              Problem
            </span>
            <p className='mt-2 max-w-lg text-base font-semibold leading-snug tracking-[-0.018em] text-foreground'>
              {row.problem}
            </p>
          </div>

          <div
            className={cn(
              'relative flex min-h-11 shrink-0 items-center justify-center',
              'border-b border-border/45 py-3',
              'md:min-h-0 md:border-b-0 md:border-border/35 md:py-0',
              'md:before:absolute md:before:inset-y-3 md:before:left-0 md:before:w-px md:before:bg-border/50',
              'md:after:absolute md:after:inset-y-3 md:after:right-0 md:after:w-px md:after:bg-border/50',
            )}
            aria-hidden='true'
          >
            <ArrowRight className='size-4.5 rotate-90 text-muted-foreground/40 md:rotate-0' strokeWidth={2} />
          </div>

          <div className='min-w-0 pt-4 md:border-l md:border-border/35 md:pl-6 md:pt-0'>
            <span className='text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground'>
              Best fit
            </span>
            <p
              className={cn(
                'mt-2 text-lg font-bold leading-snug tracking-[-0.02em]',
                emphasize ? 'text-[hsl(var(--blog-link))]' : 'text-foreground',
              )}
            >
              {row.bestFit}
            </p>
          </div>
        </div>
      </div>
    </li>
  );
};

interface IDecisionFrameworkSectionProps {
  config: IDecisionFrameworkConfig;
  /** GA4 `blog_cta_click.page` — 포스트 슬러그 */
  analyticsPageSlug?: string;
  /** 기본 `bottom_cta` */
  analyticsSection?: string;
}

const DecisionFrameworkSection = ({
  config,
  analyticsPageSlug,
  analyticsSection = 'bottom_cta',
}: IDecisionFrameworkSectionProps) => {
  const { rows, midSectionCta } = config;

  return (
    <section
      className='decision-framework-react not-prose my-14 scroll-mt-10 md:my-20'
      aria-labelledby='decision-framework-heading'
      lang='en'
    >
      <h2
        id='decision-framework-heading'
        className='mb-8 text-[clamp(1.35rem,3.5vw,1.75rem)] font-bold tracking-[-0.03em] text-foreground'
      >
        Decision Framework
      </h2>

      <ul className='flex flex-col gap-4 sm:gap-5' role='list'>
        {rows.map(row => (
          <DecisionFrameworkItem key={row.id} row={row} />
        ))}
      </ul>

      {midSectionCta ? (
        <div className='mt-12 border-t border-border/45 pt-12 md:mt-14 md:pt-14'>
          <div className='mx-auto max-w-3xl px-0 text-center sm:px-4'>
            <p
              id='decision-framework-mid-lead'
              className='text-balance text-lg font-semibold leading-snug tracking-[-0.02em] text-foreground'
            >
              {midSectionCta.lead}
            </p>
            <FunnelCtaNavigation
              compact
              className='mt-10 justify-center md:mt-11'
              aria-labelledby='decision-framework-mid-lead'
              actions={midSectionCta.actions}
              analyticsPageSlug={analyticsPageSlug}
              analyticsSection={analyticsSection}
              location='mid'
            />
          </div>
        </div>
      ) : null}
    </section>
  );
};

export { DecisionFrameworkItem };
export default DecisionFrameworkSection;
